// BE API tipovi
export type QuickCheckStatus = "GREEN" | "YELLOW" | "RED";
export type QuickCheckDocumentType = "BETREIBUNG" | "KRIF" | "ZEK";

export interface UserReference {
  id: number;
  fullName: string;
  profileImage: string;
}

export interface QuickCheckInfo {
  active: boolean;
  createdBy: UserReference;
  createdAt: string;
  modifiedBy?: UserReference;
  modifiedAt?: string;
  deletedBy?: UserReference;
  deletedAt?: string;
}

// Response od BE API-ja
export interface QuickCheckApiResponse {
  id: string;
  client: {
    id: number;
    fullName: string;
    email: string;
    countryCode: string;
    phoneNumber: string;
    profileImage: string;
    numberOfComments: number;
    type: "CLIENT" | "EMPLOYEE";
    blacklisted: boolean;
  };
  type: QuickCheckDocumentType;
  status: QuickCheckStatus;
  name: string;
  extension: string;
  path: string;
  points?: number;
  blockedUntil?: string;
  info: QuickCheckInfo;
}

// Request za izmenu Quick Check-a
export interface ChangeQuickCheckRequest {
  name: string;
  status?: QuickCheckStatus;
  points?: number;
  blockedUntil?: string;
}

// FE transformisan tip za lakši rad u komponentama
export interface QuickCheckDocument {
  id?: string;
  type: QuickCheckDocumentType;
  status: QuickCheckStatus | null; // null = dokument ne postoji
  fileName?: string;
  extension?: string;
  filePath?: string;
  points?: number;
  blockedUntil?: string;
  createdByName?: string;
  createdAt?: string;
}

// FE wrapper sa svim dokumentima za klijenta
export interface QuickCheckData {
  clientId: number;
  documents: QuickCheckDocument[];
  blacklisted: boolean;
}

// Helper za određivanje ukupnog statusa (worst-case logic)
export const calculateOverallStatus = (
  documents: QuickCheckDocument[],
): QuickCheckStatus | null => {
  const existingDocs = documents.filter((doc) => doc.status !== null);

  if (existingDocs.length === 0) return null;

  const hasRed = existingDocs.some((doc) => doc.status === "RED");
  if (hasRed) return "RED";

  const hasYellow = existingDocs.some((doc) => doc.status === "YELLOW");
  if (hasYellow) return "YELLOW";

  return "GREEN";
};

// Helper za boje statusa
export const STATUS_COLORS: Record<QuickCheckStatus | "NONE", string> = {
  GREEN: "var(--mantine-color-primary-6)",
  YELLOW: "var(--mantine-color-yellow-5)",
  RED: "var(--mantine-color-red-6)",
  NONE: "var(--mantine-color-gray-4)",
};

// Helper za transformaciju BE response-a u FE format
export const transformApiResponse = (
  clientId: number,
  apiDocs: QuickCheckApiResponse[],
): QuickCheckData => {
  const docTypes: QuickCheckDocumentType[] = ["BETREIBUNG", "KRIF", "ZEK"];

  // Defensive check: ensure apiDocs is an array
  const safeApiDocs = Array.isArray(apiDocs) ? apiDocs : [];

  // Izvuci blacklisted iz prvog dokumenta (svi dokumenti imaju istog klijenta)
  const blacklisted = safeApiDocs.length > 0 ? safeApiDocs[0].client?.blacklisted : false;

  const documents: QuickCheckDocument[] = docTypes.map((type) => {
    const apiDoc = safeApiDocs.find((d) => d.type === type);

    if (apiDoc) {
      return {
        id: apiDoc.id,
        type: apiDoc.type,
        status: apiDoc.status,
        fileName: apiDoc.name,
        extension: apiDoc.extension,
        filePath: apiDoc.path,
        points: apiDoc.points,
        blockedUntil: apiDoc.blockedUntil,
        createdByName: apiDoc.info?.createdBy?.fullName,
        createdAt: apiDoc.info?.createdAt,
      };
    }

    return {
      type,
      status: null,
    };
  });

  return {
    clientId,
    documents,
    blacklisted,
  };
};
